import extendMUITheme from "@mui/material/styles/experimental_extendTheme";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import merge from "lodash/merge";
import TOKENS, { EDionColorGroups, EDionColorNames } from "../tokens/tokens";
import { CALENDAR_THEME_OVERRIDES, DION_SCROLLBAR } from "./constants";
const { light, dark } = TOKENS.theme;
const THEME_PALETTE_DARK = makePalette(dark.color);
const THEME_PALETTE_LIGHT = makePalette(light.color);
const KEY_MAPPING = {
  "common": "dionCommon"
};
const EXTRA_KEYS = ["error"];
const EXCLUDE_KEYS = ["lines", "text", "static", "dionCommon", "gradients"];
const FONT_WEIGHTS = {
  Bold: 700,
  Medium: 500,
  Book: 400,
  Light: 300
};
const FONT_WEIGHT_KEYS = Object.keys(FONT_WEIGHTS);
const FONT_WEIGHTS_BY_VALUE = FONT_WEIGHT_KEYS.reduce((acc, item) => ({ ...acc, [FONT_WEIGHTS[item]]: item }), {});
function makePalette(tokens) {
  if (!tokens.common?.lowAccent)
    tokens.common.lowAccent = tokens.common.transparent;
  if (!tokens.common?.main)
    tokens.common.main = tokens.elevation0.main;
  if (!tokens.common?.light)
    tokens.common.light = tokens.elevation0.light;
  if (!tokens.common?.dark)
    tokens.common.dark = tokens.elevation0.dark;
  const basicColors = {
    contrastText: get(tokens.elevation0, "contrast", tokens.common.contrast),
    error: get(tokens.error, "main", tokens.common.contrast),
    success: get(tokens.success, "main", tokens.common.contrast),
    warning: get(tokens.warning, "main", tokens.common.contrast),
    info: get(tokens.info, "main", tokens.common.contrast)
  };
  const colorsData = Object.values(EDionColorGroups).reduce((result, coloGroup) => {
    result[coloGroup] = { ...tokens.common, ...tokens[coloGroup], contrastText: tokens[coloGroup]?.contrast, main: tokens[coloGroup]?.main };
    return result;
  }, {});
  return {
    dionCommon: {
      ...tokens.common,
      contrastText: tokens.common.contrast
    },
    onBrand: {
      ...basicColors,
      ...tokens.common,
      ...tokens.onBrand,
      contrastText: tokens.onBrand.contrast
    },
    divider: tokens.lines.divider,
    lines: {
      ...tokens.common,
      ...tokens.lines
    },
    special: {
      ...basicColors,
      // ...tokens.text,
      ...tokens.common,
      ...tokens.brand,
      contrastText: tokens.brand.contrast,
      main: "linear-gradient(71.68deg, #1952E8 13.38%, #552BAF 76.42%, #552BAF 91.53%)"
    },
    background: {
      default: tokens.elevation0.main,
      paper: tokens.elevation0.main
    },
    static: {
      ...tokens.static,
      darkMidContrast: `${tokens.static?.darkLowContrast.substring(0, 7) ?? "#000000"}99`,
      lightMidContrast: `${tokens.static?.lightLowContrast.substring(0, 7) ?? "#ffffff"}99`
    },
    gradients: {
      ...tokens.gradients
    },
    ...colorsData
  };
}
const NO_UNDERLINE = {
  "&:before": {
    borderBottom: "none"
  },
  "&:before:hover": {
    borderBottom: "none"
  },
  "&:hover:not(.Mui-disabled, .Mui-error):before": {
    borderBottom: "none"
  },
  "&.Mui-focused:after": {
    borderBottom: "none"
  }
};
const getComponents = () => {
  const NAME_OVERRIDES = {
    spacing: "spacings"
  };
  return Object.entries(dark).reduce((acc, [key, value]) => {
    if (NAME_OVERRIDES[key]) {
      acc[NAME_OVERRIDES[key]] = value;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};
const THEME_VARS = {
  ...getComponents(),
  typography: {
    fontFamily: '"VTB Group UI", "Roboto", "Helvetica", "Arial", sans-serif',
    allVariants: {
      fontFamily: '"VTB Group UI", "Roboto", "Helvetica", "Arial", sans-serif',
      textTransform: "none"
    },
    ...TOKENS.typography
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "unset"
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: (props) => ({
          backgroundColor: "var(--mui-palette-dionCommon-press)",
          animation: props.animate ? "" : "pulse"
        })
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          ...DION_SCROLLBAR
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          ...DION_SCROLLBAR
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          ...NO_UNDERLINE
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        root: {
          ...NO_UNDERLINE
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
          border: "1px solid var(--mui-palette-lines-divider)"
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          // borderBottom: '1px solid var(--mui-palette-lines-border)',
          ".MuiTabs-indicator": {
            backgroundColor: "var(--mui-palette-brand-main)"
          }
        }
      }
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          textColor: "var(--mui-elevation0-lowContrast)",
          "&.Mui-selected": {
            color: "var(--mui-elevation0-contrast)"
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            pointerEvents: "unset",
            backgroundColor: "inherit",
            color: "inherit",
            textDecoration: "initial"
          },
          "&.Mui-disabled": {
            pointerEvents: "unset",
            cursor: "not-allowed",
            backgroundColor: "inherit",
            color: "unset",
            "&:hover": {
              textDecoration: "initial",
              pointerEvents: "unset",
              backgroundColor: "inherit",
              color: "unset"
            }
          },
          transition: "none"
        }
      },
      variants: [
        {
          props: { variant: "text" },
          style: {
            "&:hover": {
              backgroundColor: "inherit",
              // отключаем изменение фона при наведении
              color: "inherit",
              // отключаем изменение цвета текста при наведении
              textDecoration: "initial"
            }
          }
        }
      ]
      // variants: [
      //   {
      //     props: { variant: 'dashed' },
      //     style: {
      //       textTransform: 'none',
      //       border: `2px dashed ${blue[500]}`,
      //     },
      //   },
      //   {
      //     props: { variant: 'dashed', color: 'secondary' },
      //     style: {
      //       border: `4px dashed ${red[500]}`,
      //     },
      //   },
      // ],
    },
    ...CALENDAR_THEME_OVERRIDES
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 390,
      md: 758,
      lg: 1280,
      xl: 1440
    }
  },
  colorSchemes: {
    dark: {
      // palette for dark mode
      palette: {
        ...THEME_PALETTE_DARK
      }
    },
    light: {
      // palette for light mode
      palette: {
        ...THEME_PALETTE_LIGHT
      }
    }
  }
};
const GLOBAL_STYLES = {
  ":root": {
    "--font-family": "VTBGroupUIWebBook, sans-serif"
  },
  html: {
    fontFamily: "var(--font-family)",
    // TODO
    fontWeight: "var(--dion-font-semi-bold)",
    // TODO
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    fontSmooth: "auto",
    color: "var(--mui-palette-elevation0-contrast)"
  },
  body: {
    background: "var(--mui-palette-elevation0-main)"
  }
};
const GLOBAL_STYLES_LIGHT = {
  ...GLOBAL_STYLES
};
const GLOBAL_STYLES_DARK = {
  ...GLOBAL_STYLES
};
const mapTokens2ThemeColors = (tokens, theme) => Object.keys(tokens).concat(EXTRA_KEYS).map((key) => KEY_MAPPING[key] || key).filter((key) => theme[key] && !EXCLUDE_KEYS.includes(key));
let THEME = extendMUITheme(THEME_VARS);
const COLOR_KEYS = Object.values(EDionColorGroups);
const COLOR_NAMES = Object.values(EDionColorNames);
const BRAND_COLORS = ["brand", "blueElevation1", "blueElevation2", "special"];
const getTheme = (themeVars, forceCustomTheme) => {
  if (themeVars === true)
    THEME = extendMUITheme(THEME_VARS);
  if (isObject(themeVars) && !isEmpty(themeVars)) {
    if (forceCustomTheme) {
      THEME = extendMUITheme(merge(THEME_VARS, themeVars));
    } else {
      THEME = extendMUITheme(merge(themeVars, THEME_VARS));
    }
  }
  return THEME;
};
var theme_default = THEME;
export {
  BRAND_COLORS,
  COLOR_KEYS,
  COLOR_NAMES,
  DION_SCROLLBAR,
  FONT_WEIGHTS,
  FONT_WEIGHTS_BY_VALUE,
  GLOBAL_STYLES,
  GLOBAL_STYLES_DARK,
  GLOBAL_STYLES_LIGHT,
  THEME_VARS,
  theme_default as default,
  getTheme
};
